import { Capacitor } from '@capacitor/core'
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric'

export const BIOMETRIC_SERVER = 'ca.pnlsystem.app'
const ENABLED_KEY = 'biometric_login_enabled'

export type BiometricKind = 'face' | 'touch' | 'biometric'

export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (Capacitor.isNativePlatform()) return true
    const platform = Capacitor.getPlatform()
    return platform === 'ios' || platform === 'android'
  } catch {
    const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    return Boolean(cap?.isNativePlatform?.())
  }
}

export async function getBiometricKind(): Promise<BiometricKind | null> {
  if (!isNativeApp()) return null
  try {
    const result = await NativeBiometric.isAvailable({ useFallback: false })
    if (!result.isAvailable) return null
    if (result.biometryType === BiometryType.FACE_ID || result.biometryType === BiometryType.FACE_AUTHENTICATION) {
      return 'face'
    }
    if (result.biometryType === BiometryType.TOUCH_ID || result.biometryType === BiometryType.FINGERPRINT) {
      return 'touch'
    }
    return 'biometric'
  } catch {
    return null
  }
}

export async function hasStoredBiometricCredentials(): Promise<boolean> {
  if (!isNativeApp()) return false
  try {
    const result = await NativeBiometric.isCredentialsSaved({ server: BIOMETRIC_SERVER })
    return Boolean(result.isSaved)
  } catch {
    return false
  }
}

export async function saveBiometricCredentials(identifier: string, pin: string): Promise<void> {
  if (!isNativeApp()) return
  await NativeBiometric.setCredentials({
    username: identifier,
    password: pin,
    server: BIOMETRIC_SERVER,
  })
  localStorage.setItem(ENABLED_KEY, '1')
}

export async function clearBiometricCredentials(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER })
  } catch {
    // No saved credentials — ignore.
  }
  localStorage.removeItem(ENABLED_KEY)
}

export async function readBiometricCredentials(): Promise<{ identifier: string; pin: string } | null> {
  if (!isNativeApp()) return null
  await NativeBiometric.verifyIdentity({
    reason: 'Sign in to ManaPNL',
    title: 'ManaPNL',
    subtitle: 'Verify your identity',
    description: 'Use Face ID to sign in quickly',
  })
  const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER })
  if (!credentials.username || !credentials.password) return null
  return { identifier: credentials.username, pin: credentials.password }
}
